import { useState, useRef, useMemo, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { emails as mockEmails } from '@/data/mockData';
import type { Email } from '@/types';
import { useTenant } from '@/hooks/useTenant';
import { useEmails, type SyncedEmail } from '@/hooks/useEmails';
import { markEmailRead, useUnreadEmailCount } from '@/hooks/useUnreadEmailCount';
import { useDashboardData } from '@/hooks/useSyncedData';
import { useProjectCommunicationSignals } from '@/hooks/useProjectCommunicationSignals';
import {
  Star,
  StarOff,
  AlertTriangle,
  CheckCircle2,
  Minus,
  ArrowRight,
  ListTodo,
  Send,
  Reply,
  Paperclip,
  X,
  Wand2,
  Loader2,
  Shield,
  TrendingUp,
  TrendingDown,
  Activity,
  Info,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import SmartTaskSuggestions from '@/components/tasks/SmartTaskSuggestions';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Import email sender avatars
import sarahChenAvatar from '@/assets/inbox/sarah-chen-email.png';
import michaelTorresAvatar from '@/assets/inbox/michael-torres.png';
import alexKimAvatar from '@/assets/inbox/alex-kim.png';
import emilyWatsonAvatar from '@/assets/inbox/emily-watson.png';

// Avatar mapping for email senders
const emailAvatars: Record<string, string> = {
  'Sarah Chen': sarahChenAvatar,
  'Michael Torres': michaelTorresAvatar,
  'Alex Kim': alexKimAvatar,
  'Emily Watson': emilyWatsonAvatar,
};

// `action_items.source_email_id` is a UUID FK to `emails_sync(id)`. Demo/mock
// emails (src/data/mockData.ts) use plain string ids like '1', which aren't
// valid UUIDs and 22P02 when used in an insert or an `.eq()` filter against a
// UUID column. Used to gate both the `emailId` passed down to
// SmartTaskSuggestions and the linkedOpenActions query below so demo-tenant
// rows are treated as "no email linkage available" instead of hard-failing.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SmartInbox = () => {
  const { isDemoTenant, demoMockDataEnabled } = useTenant();
  const showMock = isDemoTenant && demoMockDataEnabled;
  const [selectedProjectFilter, setSelectedProjectFilter] = useState<string | null>(null);
  // Scoped to the current project filter: the newest-50 cap applies within
  // the selected project, not globally, so a project whose emails aren't in
  // the overall newest 50 is still reachable once selected.
  const { emails, isLoadingEmails, reload: loadEmails, setEmails } = useEmails(selectedProjectFilter);
  const [selectedEmail, setSelectedEmail] = useState<SyncedEmail | null>(() =>
    showMock
      ? (mockEmails[0]
          ? {
              ...mockEmails[0],
              project_id: null,
              suggested_project_id: null,
              match_confidence: null,
              matched_via: null,
              match_status: 'unmatched',
              direction: null,
              conversation_id: null,
              is_read: true,
              read_at: null,
              external_id: '',
            }
          : null)
      : null
  );
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isGeneratingReply, setIsGeneratingReply] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [linkedOpenActions, setLinkedOpenActions] = useState<{ id: string; title: string }[]>([]);
  const [checkedActionIds, setCheckedActionIds] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { settings } = useTenantSettings();
  const { projects } = useDashboardData();
  const { signals: allProjectSignals, getByProjectId, reload: reloadSignals } = useProjectCommunicationSignals();
  const { refetch: refetchUnreadCount } = useUnreadEmailCount();
  const [groupByProject, setGroupByProject] = useState(false);
  const [isUpdatingMatch, setIsUpdatingMatch] = useState(false);

  // Which projects have at least one linked email, for the filter dropdown.
  // This must come from an UNFILTERED view — once `emails` above is scoped
  // to a single selectedProjectFilter (Fix I2), it can no longer answer
  // "does project X have any emails at all" for every other project.
  //
  // Sourced from `allProjectSignals` (project_communication_signals, already
  // loaded above via useProjectCommunicationSignals with no .limit()) rather
  // than a dedicated emails_sync query: compute_project_communication_signals()
  // (migration 096) writes exactly one row per DISTINCT project_id found
  // among the tenant's emails_sync rows with project_id IS NOT NULL — i.e.
  // one row per project with >=1 linked email, which is precisely what this
  // dropdown needs, already deduplicated and with no arbitrary row cap to
  // worry about (unlike emails_sync itself, which is exactly the unbounded
  // set this fix must not re-introduce a cap-related blind spot into). This
  // does mean the dropdown reflects the last computed-signals run rather
  // than instantaneous emails_sync state — acceptable since that computation
  // already runs after every outlook sync (see a21c3c9).
  const projectsWithEmails = useMemo(() => {
    const linkedProjectIds = new Set(allProjectSignals.map((s) => s.project_id));
    return projects.filter((p) => p.syncRowId && linkedProjectIds.has(p.syncRowId));
  }, [projects, allProjectSignals]);

  // `emails` from useEmails() above is already scoped to selectedProjectFilter
  // server-side (Fix I2), so this is now just an identity pass-through, kept
  // as its own binding (rather than renaming every `filteredEmails` usage
  // below) so a future defensive re-filter is a one-line change here.
  const filteredEmails = emails;

  // Buckets of filteredEmails by project, used only when groupByProject is on.
  const groupedEmails = useMemo(() => {
    if (!groupByProject) return null;
    const order: string[] = [];
    const groups = new Map<string, { key: string; name: string; emails: SyncedEmail[] }>();
    for (const email of filteredEmails) {
      const key = email.project_id ?? '__unlinked__';
      if (!groups.has(key)) {
        const name = email.project_id
          ? projects.find((p) => p.syncRowId === email.project_id)?.name ?? 'Unknown project'
          : 'Unlinked';
        groups.set(key, { key, name, emails: [] });
        order.push(key);
      }
      groups.get(key)!.emails.push(email);
    }
    return order.map((key) => groups.get(key)!);
  }, [groupByProject, filteredEmails, projects]);

  // Keep selectedEmail in sync with the currently filtered emails list
  // whenever it changes (initial load, refresh, or project filter change) —
  // mirrors the sync logic that previously lived inline in loadEmails,
  // before data loading moved into useEmails. Falls back to the first email
  // in the filtered set if the current selection falls outside the filter.
  useEffect(() => {
    setSelectedEmail((prev) =>
      (prev && filteredEmails.some((e) => e.id === prev.id)) ? prev : (filteredEmails[0] ?? null)
    );
  }, [filteredEmails]);

  // Only a real UUID (see UUID_RE above) can be used against
  // action_items.source_email_id — demo/mock emails resolve to null here,
  // which both the linkedOpenActions effect below and the SmartTaskSuggestions
  // emailId prop treat as "no email linkage available".
  const linkedEmailId = selectedEmail && UUID_RE.test(selectedEmail.id) ? selectedEmail.id : null;

  // Open action items linked to the selected email (via
  // action_items.source_email_id), offered as a pre-send checklist in the
  // reply composer so a reply that resolves an action can mark it complete
  // in the same step. Reloaded whenever the composer opens or the selected
  // email changes.
  useEffect(() => {
    if (!isReplying || !linkedEmailId) {
      setLinkedOpenActions([]);
      setCheckedActionIds(new Set());
      return;
    }
    supabase
      .from('action_items')
      .select('id, title')
      .eq('source_email_id', linkedEmailId)
      .in('status', ['suggested', 'approved'])
      .then(({ data, error }) => {
        if (error) {
          console.error('Failed to load linked actions:', error);
          return;
        }
        setLinkedOpenActions(data ?? []);
      });
  }, [isReplying, linkedEmailId]);

  // Calculate aggregate signals for the "All projects" default view.
  // `sentiment.label` is a permanently-mocked field (never computed from
  // real data, even before this branch) — "Total Signals" and "Priority
  // Alerts" are real (escalation_flag/priority-driven), but the sentiment
  // aggregates that used to sit alongside them are not, and are replaced
  // below with real cross-project totals from project_communication_signals
  // (unanswered_count / escalation_count), summed across every project row
  // the tenant currently has computed. This keeps the default view's
  // numbers consistent with what the governance banner claims ("real
  // per-project communication signals"), matching the per-project view's
  // rule-based-aggregates-only constraint.
  const aggregateSignals = useMemo(() => {
    const total = emails.length;
    const critical = emails.filter(e => e.priority === 'critical').length;
    const high = emails.filter(e => e.priority === 'high').length;
    const totalUnanswered = allProjectSignals.reduce((sum, s) => sum + (s.unanswered_count ?? 0), 0);
    const totalEscalations = allProjectSignals.reduce((sum, s) => sum + (s.escalation_count ?? 0), 0);

    return {
      total,
      priorityAlerts: critical + high,
      criticalCount: critical,
      totalUnanswered,
      totalEscalations,
    };
  }, [emails, allProjectSignals]);

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast({ title: 'Error', description: 'Please enter a message', variant: 'destructive' });
      return;
    }
    if (!selectedEmail) return;
    if (!selectedEmail.external_id) {
      toast({
        title: 'Error',
        description: 'This email has no Outlook message id on record, so a reply cannot be sent.',
        variant: 'destructive',
      });
      return;
    }

    setIsSendingReply(true);
    try {
      // emails_sync rows don't carry the integration_configs row that
      // produced them (no integration_id column — see SyncedEmail's
      // external_id comment in useEmails.ts), so the tenant's active
      // 'outlook' integration is resolved here instead, at send-time.
      const { data: outlookIntegration, error: integrationError } = await supabase
        .from('integration_configs')
        .select('id')
        .eq('integration_type', 'outlook')
        .eq('is_active', true)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (integrationError) throw integrationError;
      if (!outlookIntegration) {
        throw new Error('No active Outlook integration found for this tenant. Connect Microsoft 365 first.');
      }

      const { error } = await supabase.functions.invoke('microsoft-graph-connector', {
        body: {
          service: 'outlook',
          action: 'reply',
          integrationId: outlookIntegration.id,
          messageExternalId: selectedEmail.external_id,
          comment: replyText,
        },
      });

      if (error) {
        // supabase-js's FunctionsHttpError only surfaces a generic
        // "Edge Function returned a non-2xx status code" in error.message —
        // the connector's actual message (e.g. the real HTTP status + Graph
        // error text) lives in error.context, the raw Response. Same
        // extraction pattern as PresentationTemplates.tsx's generate mutation.
        let message = error.message;
        try {
          const body = await (error as { context?: Response }).context?.json();
          if (body?.error) message = body.error;
        } catch {
          // response body wasn't JSON (or already consumed) — fall back to error.message
        }
        throw new Error(message);
      }

      // .select() + length check mirrors handleConfirmMatch/handleDismissMatch
      // above: action_items' RLS UPDATE policy (migration 087) only allows
      // project_manager and above, so a lower-privileged caller's UPDATE
      // silently matches zero rows with error: null. The email has already
      // sent successfully by this point, so a partial failure here surfaces
      // as its own toast rather than blocking/failing the whole send.
      // Emit exactly ONE toast combining action-completion + reply-sent status
      // to avoid eviction under TOAST_LIMIT = 1.
      let toastOptions: Parameters<typeof toast>[0] = {
        title: 'Reply Sent',
        description: `Reply sent to ${selectedEmail.from}`,
      };

      if (checkedActionIds.size > 0) {
        const { data: updatedActions, error: updateError } = await supabase
          .from('action_items')
          .update({ status: 'completed' })
          .in('id', Array.from(checkedActionIds))
          .select('id');
        if (updateError) {
          console.error('Failed to mark actions complete:', updateError);
          toastOptions = {
            title: 'Reply sent, but action update failed',
            description: updateError.message,
            variant: 'destructive',
          };
        } else if ((updatedActions?.length ?? 0) < checkedActionIds.size) {
          toastOptions = {
            title: 'Reply sent, but some actions were not marked complete',
            description: 'You may not have permission to update one or more checked actions.',
            variant: 'destructive',
          };
        }
      }

      toast(toastOptions);
      setReplyText('');
      setAttachments([]);
      setIsReplying(false);
    } catch (error: unknown) {
      console.error('Error sending reply:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send reply. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleGenerateAIReply = async () => {
    if (!selectedEmail) return;
    
    setIsGeneratingReply(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-ai-reply', {
        body: {
          emailSubject: selectedEmail.subject,
          emailFrom: selectedEmail.from,
          emailBody: selectedEmail.body,
          tone: 'professional',
        },
      });

      if (error) throw error;
      
      if (data?.reply) {
        setReplyText(data.reply);
        toast({
          title: 'AI Reply Generated',
          description: 'Review and edit the suggested reply before sending.',
        });
      }
    } catch (error: unknown) {
      console.error('Error generating AI reply:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate AI reply. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingReply(false);
    }
  };

  const handleConfirmMatch = async () => {
    if (!selectedEmail) return;
    setIsUpdatingMatch(true);
    try {
      // .select() + empty-check mirrors useProjectEmailMappings.ts's
      // removeMapping: an RLS-denied UPDATE returns { error: null, data: [] }
      // rather than an error, since PostgREST simply matches zero rows under
      // RLS. Without the empty-check, a caller lacking the reviewer role
      // (see migration 097) would see a false "Match confirmed" success toast
      // despite nothing actually being written.
      const { data, error } = await supabase
        .from('emails_sync')
        .update({
          project_id: selectedEmail.suggested_project_id,
          match_status: 'confirmed',
          matched_via: 'manual',
        })
        .eq('id', selectedEmail.id)
        .select();

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('You do not have permission to update this email\'s match status.');
      }

      // Re-fetches whatever project_communication_signals rows currently
      // exist in the DB. This does NOT recompute signals — that table is
      // only written by the scheduled outlook-sync job (migration 096's
      // compute function is service-role only), so a manual confirm here
      // can't move the underlying numbers immediately. It just ensures the
      // UI isn't showing signals staler than the last successful
      // computation, without requiring a full page remount.
      await loadEmails();
      await reloadSignals();
      toast({
        title: 'Match confirmed',
        description: 'This email has been linked to the suggested project.',
      });
    } catch (error: unknown) {
      console.error('Error confirming project match:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to confirm the project match. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingMatch(false);
    }
  };

  const handleDismissMatch = async () => {
    if (!selectedEmail) return;
    setIsUpdatingMatch(true);
    try {
      // Same .select() + empty-check pattern as handleConfirmMatch above /
      // useProjectEmailMappings.ts's removeMapping — see that comment for why.
      const { data, error } = await supabase
        .from('emails_sync')
        .update({ match_status: 'rejected' })
        .eq('id', selectedEmail.id)
        .select();

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('You do not have permission to update this email\'s match status.');
      }

      await loadEmails();
      toast({
        title: 'Match dismissed',
        description: 'The suggested project match was dismissed.',
      });
    } catch (error: unknown) {
      console.error('Error dismissing project match:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to dismiss the project match. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingMatch(false);
    }
  };

  // Human-readable duration for avg_response_time_seconds, e.g. "2h 15m", "3d 4h".
  const formatDuration = (seconds: number | null): string => {
    if (seconds == null) return '—';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${seconds}s`;
  };

  const getSuggestedProjectName = (email: SyncedEmail): string => {
    if (!email.suggested_project_id) return 'Unknown project';
    return projects.find((p) => p.syncRowId === email.suggested_project_id)?.name
      ?? email.suggested_project_id;
  };

  const getPriorityColor = (priority: Email['priority']) => {
    switch (priority) {
      case 'critical':
        return 'masira-badge-danger';
      case 'high':
        return 'masira-badge-warning';
      case 'medium':
        return 'bg-primary/10 text-primary';
      default:
        return 'masira-badge-neutral';
    }
  };

  const getEscalationLabel = (level: Email['escalationLevel']) => {
    switch (level) {
      case 'L1':
        return 'Operational';
      case 'L2':
        return 'Tactical';
      case 'L3':
        return 'Strategic';
      case 'L4':
        return 'Executive';
    }
  };

  // Marks the email read (no-op if it's already read) and forces an
  // immediate unread-badge recount rather than waiting for
  // useUnreadEmailCount's next poll tick. Because useUnreadEmailCount now
  // shares a single module-scoped count across every mounted instance (see
  // that hook), this refetch() also updates the Sidebar's badge immediately,
  // not just this component's own copy.
  const handleSelectEmail = (email: SyncedEmail) => {
    setSelectedEmail(email);
    if (!email.is_read) {
      markEmailRead(email.id).then((result) => {
        if (!result.ok) return;
        refetchUnreadCount();
        // Only apply the optimistic local patch if the write actually took
        // effect — an RLS-denied update (base 'user' role, see migration 097)
        // returns { ok: true, updated: false } and must NOT be reflected as
        // "read" in the UI, or the row's unread styling would clear locally
        // while the DB still has it unread (reverting confusingly on the
        // next poll).
        if (!result.updated) return;
        const readAt = new Date().toISOString();
        const patch = (e: SyncedEmail): SyncedEmail =>
          e.id === email.id ? { ...e, is_read: true, isRead: true, read_at: readAt } : e;
        setEmails((prev) => prev.map(patch));
        setSelectedEmail((prev) => (prev && prev.id === email.id ? patch(prev) : prev));
      });
    }
  };

  const renderEmailCard = (email: SyncedEmail) => (
    <button
      key={email.id}
      onClick={() => handleSelectEmail(email)}
      className={cn(
        'w-full p-4 text-start border-b border-border transition-colors',
        'hover:bg-secondary/50',
        selectedEmail?.id === email.id && 'bg-secondary',
        !email.isRead && 'bg-primary/5'
      )}
    >
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage src={emailAvatars[email.from]} alt={email.from} />
          <AvatarFallback className="bg-primary/10 text-primary text-sm">
            {email.from.split(' ').map(n => n[0]).join('')}
          </AvatarFallback>
        </Avatar>
        <button className="mt-1 text-muted-foreground hover:text-warning">
          {email.isStarred ? (
            <Star className="w-4 h-4 fill-warning text-warning" />
          ) : (
            <StarOff className="w-4 h-4" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={cn('font-medium text-foreground', !email.isRead && 'font-semibold')}>
              {email.from}
            </span>
            <span className={cn('masira-badge text-[10px]', getPriorityColor(email.priority))}>
              {email.priority}
            </span>
          </div>
          <p className={cn('text-sm truncate', !email.isRead ? 'text-foreground' : 'text-muted-foreground')}>
            {email.subject}
          </p>
          <p className="text-xs text-muted-foreground truncate mt-1">{email.preview}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground">{getEscalationLabel(email.escalationLevel)}</span>
          </div>
        </div>
      </div>
    </button>
  );

  if (!settings.smartInboxEnabled) {
    return (
      <div className="space-y-6">
        <div className="masira-fade-in">
          <h2 className="text-2xl font-bold text-foreground">Communication Signals</h2>
          <p className="text-muted-foreground">Feature disabled by Tenant Administrator</p>
        </div>
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            The Smart Inbox feature has been disabled by your organization's administrator. 
            Contact your Tenant Admin for more information.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="masira-fade-in flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Communication Signals</h2>
          <p className="text-muted-foreground">Aggregate communication intelligence from connected systems</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={selectedProjectFilter ?? 'all'}
            onValueChange={(value) => setSelectedProjectFilter(value === 'all' ? null : value)}
          >
            <SelectTrigger className="w-[200px] h-9">
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projectsWithEmails.map((p) => (
                <SelectItem key={p.syncRowId} value={p.syncRowId as string}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={groupByProject ? 'default' : 'outline'}
            size="sm"
            onClick={() => setGroupByProject((g) => !g)}
          >
            Group by project
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              loadEmails();
              reloadSignals();
            }}
            disabled={isLoadingEmails}
          >
            <RefreshCw className={cn('w-4 h-4 me-2', isLoadingEmails && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Governance Notice */}
      <Alert className="border-primary/30 bg-primary/5">
        <Shield className="h-4 w-4 text-primary" />
        <AlertDescription className="text-sm">
          <span className="font-medium">Governed Feature:</span> This view shows aggregate communication signals only.
          Individual sentiment scoring is disabled. Selecting a project shows real per-project communication signals
          (unanswered threads, response time, escalations, volume trend). Data is not used for HR or performance evaluation.
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 ms-1 inline-block cursor-help text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  Per governance policy, this feature aggregates signals to support PMO decision-making 
                  without individual performance tracking.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </AlertDescription>
      </Alert>

      {/* Signal Cards */}
      {selectedProjectFilter ? (
        (() => {
          const projectSignals = getByProjectId(selectedProjectFilter);
          return (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="masira-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  <span className="text-xs font-medium text-muted-foreground">Unanswered Threads</span>
                </div>
                <div className="text-2xl font-bold text-foreground">{projectSignals?.unanswered_count ?? '—'}</div>
                <p className="text-xs text-muted-foreground">Awaiting a reply</p>
              </div>
              <div className="masira-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-4 h-4 text-primary" />
                  <span className="text-xs font-medium text-muted-foreground">Avg Response Time</span>
                </div>
                <div className="text-2xl font-bold text-foreground">
                  {formatDuration(projectSignals?.avg_response_time_seconds ?? null)}
                </div>
                <p className="text-xs text-muted-foreground">Time to first reply</p>
              </div>
              <div className="masira-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                  <span className="text-xs font-medium text-muted-foreground">Escalations</span>
                </div>
                <div className="text-2xl font-bold text-foreground">{projectSignals?.escalation_count ?? 0}</div>
                <p className="text-xs text-muted-foreground">Flagged communications</p>
              </div>
              <div className="masira-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  {projectSignals?.volume_trend === 'up' ? (
                    <TrendingUp className="w-4 h-4 text-success" />
                  ) : projectSignals?.volume_trend === 'down' ? (
                    <TrendingDown className="w-4 h-4 text-destructive" />
                  ) : (
                    <Minus className="w-4 h-4 text-warning" />
                  )}
                  <span className="text-xs font-medium text-muted-foreground">Volume Trend</span>
                </div>
                <div className="text-2xl font-bold text-foreground capitalize">
                  {projectSignals?.volume_trend ?? '—'}
                </div>
                <p className="text-xs text-muted-foreground">
                  Email volume: {projectSignals?.email_volume ?? 0}
                </p>
              </div>
            </div>
          );
        })()
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="masira-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium text-muted-foreground">Total Signals</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{aggregateSignals.total}</div>
            <p className="text-xs text-muted-foreground">Communications analyzed</p>
          </div>
          <div className="masira-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-warning" />
              <span className="text-xs font-medium text-muted-foreground">Total Unanswered</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{aggregateSignals.totalUnanswered}</div>
            <p className="text-xs text-muted-foreground">Threads awaiting a reply, across all projects</p>
          </div>
          <div className="masira-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-warning" />
              <span className="text-xs font-medium text-muted-foreground">Priority Alerts</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{aggregateSignals.priorityAlerts}</div>
            <p className="text-xs text-muted-foreground">High/Critical items</p>
          </div>
          <div className="masira-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              <span className="text-xs font-medium text-muted-foreground">Total Escalations</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{aggregateSignals.totalEscalations}</div>
            <p className="text-xs text-muted-foreground">Flagged communications, across all projects</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-420px)]">
        {/* Email List */}
        <div className="masira-card overflow-hidden flex flex-col">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-foreground">Signal Queue</h3>
          </div>
          <div className="flex-1 overflow-y-auto masira-scrollbar">
            {groupByProject && groupedEmails
              ? groupedEmails.map((group) => (
                  <div key={group.key}>
                    <div className="px-4 py-2 bg-secondary/40 text-xs font-semibold text-muted-foreground sticky top-0">
                      {group.name}
                    </div>
                    {group.emails.map((email) => renderEmailCard(email))}
                  </div>
                ))
              : filteredEmails.map((email) => renderEmailCard(email))}
          </div>
        </div>

        {/* Email Content + AI Analysis */}
        <div className="masira-card overflow-hidden flex flex-col">
          {selectedEmail ? (
            <>
              <div className="p-4 border-b border-border">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-12 w-12 shrink-0">
                      <AvatarImage src={emailAvatars[selectedEmail.from]} alt={selectedEmail.from} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {selectedEmail.from.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{selectedEmail.subject}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">{selectedEmail.from}</span>
                        <span>•</span>
                        <span>{selectedEmail.fromEmail}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsReplying(!isReplying)}
                    className="shrink-0"
                  >
                    <Reply className="w-4 h-4 me-2" />
                    Reply
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto masira-scrollbar">
                {/* Email Body */}
                <div className="p-4">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-foreground leading-relaxed">
                    {selectedEmail.body}
                  </pre>
                </div>

                {/* Extracted Intelligence (no individual sentiment) */}
                <div className="px-4 pb-4 space-y-3">
                  {/* Suggested project match */}
                  {selectedEmail.match_status === 'suggested' && (
                    <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-lg bg-secondary/30">
                      <div className="text-sm text-foreground">
                        Suggested match:{' '}
                        <span className="font-semibold">{getSuggestedProjectName(selectedEmail)}</span>{' '}
                        <span className="text-muted-foreground">
                          ({Math.round((selectedEmail.match_confidence ?? 0) * 100)}%)
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleDismissMatch}
                          disabled={isUpdatingMatch}
                        >
                          Dismiss
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleConfirmMatch}
                          disabled={isUpdatingMatch}
                        >
                          Confirm
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Escalation Level & Priority */}
                  <div className="flex items-center gap-4 p-3 rounded-lg bg-secondary/30">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-warning" />
                      <span className="text-xs font-medium text-muted-foreground">Classification</span>
                    </div>
                    <span className={cn('masira-badge text-xs', getPriorityColor(selectedEmail.priority))}>
                      {selectedEmail.priority} priority
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {getEscalationLabel(selectedEmail.escalationLevel)} level
                    </span>
                  </div>

                  {/* Compact Tasks */}
                  {selectedEmail.extractedTasks.length > 0 && (
                    <div className="p-3 rounded-lg bg-secondary/30">
                      <div className="flex items-center gap-2 mb-2">
                        <ListTodo className="w-4 h-4 text-primary" />
                        <span className="text-xs font-medium text-muted-foreground">Extracted Actions</span>
                      </div>
                      <div className="space-y-1.5">
                        {selectedEmail.extractedTasks.map((task, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between py-1.5 px-2 rounded bg-background/50 hover:bg-background transition-colors group"
                          >
                            <span className="text-xs text-foreground">{task}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <ArrowRight className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Task Suggestions */}
                  <div className="p-3 rounded-lg bg-secondary/30">
                    <SmartTaskSuggestions
                      content={selectedEmail.body}
                      contentType="email"
                      sender={selectedEmail.from}
                      subject={selectedEmail.subject}
                      emailId={linkedEmailId ?? undefined}
                      compact
                      onTaskAdded={(task) => {
                        console.log('Task added:', task);
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Reply Section */}
              {isReplying && (
                <div className="p-4 border-t border-border space-y-3 bg-secondary/20">
                  <div className="text-sm text-muted-foreground">
                    Replying to <span className="font-medium text-foreground">{selectedEmail.from}</span>
                  </div>
                  <Textarea
                    placeholder="Type your reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    className="min-h-[100px] resize-none"
                  />

                  {linkedOpenActions.length > 0 && (
                    <div className="space-y-1.5 border border-border rounded-lg p-2.5">
                      <div className="text-xs font-medium text-muted-foreground">Mark related actions complete on send</div>
                      {linkedOpenActions.map((action) => (
                        <label key={action.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checkedActionIds.has(action.id)}
                            onChange={(e) => {
                              setCheckedActionIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(action.id);
                                else next.delete(action.id);
                                return next;
                              });
                            }}
                          />
                          <span className="truncate">{action.title}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Attachments */}
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-lg text-sm"
                        >
                          <Paperclip className="w-3 h-3 text-muted-foreground" />
                          <span className="max-w-[120px] truncate text-foreground">{file.name}</span>
                          <button
                            onClick={() => removeAttachment(index)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="hidden"
                        multiple
                      />
                      <Button variant="ghost" size="sm" onClick={handleAttachmentClick}>
                        <Paperclip className="w-4 h-4 me-2" />
                        Attach
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleGenerateAIReply}
                        disabled={isGeneratingReply}
                        className="text-primary border-primary/30 hover:bg-primary/10"
                      >
                        {isGeneratingReply ? (
                          <Loader2 className="w-4 h-4 me-2 animate-spin" />
                        ) : (
                          <Wand2 className="w-4 h-4 me-2" />
                        )}
                        AI Reply
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => {
                        setIsReplying(false);
                        setReplyText('');
                        setAttachments([]);
                      }}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSendReply} disabled={isSendingReply}>
                        {isSendingReply ? (
                          <Loader2 className="w-4 h-4 me-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 me-2" />
                        )}
                        Send
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Select a signal to view
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartInbox;
